import { DOCUMENT } from "@angular/common";
import { inject, Injectable } from "@angular/core";

export interface FlipOptions {
    /** Element whose descendants matching `itemSelector` are animated. */
    container: HTMLElement;
    /** CSS selector for the animatable items inside `container`. */
    itemSelector: string;
    /** Stable identity for an item element, used to match its old and new position. */
    key: (el: HTMLElement) => string | null;
    /** Mutate the underlying data so the DOM will reflect the new order/membership. */
    mutate: () => void;
    /**
     * Force a synchronous DOM update after `mutate` so the new ("Last") positions
     * can be measured — e.g. `() => changeDetectorRef.detectChanges()`. Called
     * ONLY when the animation actually runs; skipped otherwise so callers can use
     * a lighter change-detection pass (e.g. markForCheck) themselves.
     */
    render: () => void;
    /** Animation duration in ms (defaults to DEFAULT_DURATION_MS). */
    durationMs?: number;
}

/**
 * Classic FLIP (First, Last, Invert, Play) animation for reordered / added /
 * removed list items. Items are animated with plain CSS transforms while staying
 * in normal document flow, so sticky or positioned siblings (headers, bars) are
 * never touched. We went this way because view transitions (a much simpler only css way to
 * do this) messed up the sticky headers filters; they were also transitioned, and because of that
 * their bg filters disappeared during transition
 *
 * Not `providedIn: "root"`: this holds per-animation state (`animating`) that is
 * logically per-list, so it is provided at the component level (one instance per
 * host view) rather than shared app-wide.
 */
@Injectable()
export class FlipService {
    private static readonly DEFAULT_DURATION_MS = 250;

    private readonly document = inject(DOCUMENT);
    private animating = false;

    /** True while a FLIP animation is playing. */
    get isAnimating(): boolean {
        return this.animating;
    }

    /**
     * Record each item's position, run `mutate` (+ `render` when animating), then
     * animate every item from its old position to its new one.
     *
     * Skips the animation — running only `mutate` — when motion is reduced or no
     * items are currently rendered (also the case in unit tests, keeping them
     * synchronous). Returns whether the animation ran.
     */
    animate(options: FlipOptions): boolean {
        const first = this.captureRects(options);
        const shouldAnimate = first.size > 0 && !this.prefersReducedMotion();

        options.mutate();

        if (!shouldAnimate) {
            return false;
        }

        // Render the new (Last) positions synchronously so we can measure them.
        options.render();
        this.play(options, first);
        return true;
    }

    private captureRects(options: FlipOptions): Map<string, DOMRect> {
        const rects = new Map<string, DOMRect>();
        options.container.querySelectorAll<HTMLElement>(options.itemSelector).forEach(el => {
            const id = options.key(el);
            if (id) rects.set(id, el.getBoundingClientRect());
        });
        return rects;
    }

    private play(options: FlipOptions, first: Map<string, DOMRect>): void {
        const duration = options.durationMs ?? FlipService.DEFAULT_DURATION_MS;
        const items = Array.from(options.container.querySelectorAll<HTMLElement>(options.itemSelector));

        // Invert: place each item back at its old position (or transparent if new).
        items.forEach(el => {
            const id = options.key(el);
            const prev = id ? first.get(id) : undefined;
            const last = el.getBoundingClientRect();
            el.style.transition = "none";
            if (prev) {
                el.style.transform = `translate(${prev.left - last.left}px, ${prev.top - last.top}px)`;
            } else {
                el.style.opacity = "0";
            }
        });

        options.container.getBoundingClientRect(); // flush the inverted styles

        // Play: release to the natural position/opacity with a transition.
        this.animating = true;
        requestAnimationFrame(() => {
            items.forEach(el => {
                el.style.transition = `transform ${duration}ms cubic-bezier(0, 0, 0.2, 1), opacity ${duration}ms ease`;
                el.style.transform = "";
                el.style.opacity = "";
                el.addEventListener("transitionend", () => (el.style.transition = ""), { once: true });
            });
        });

        setTimeout(() => (this.animating = false), duration + 50);
    }

    private prefersReducedMotion(): boolean {
        return this.document.defaultView?.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    }
}
